import type { ContactMongoFilter } from './contacts.types';
import { FILTERABLE_FIELDS, type FilterFieldConfig, type FilterValue } from '../config/filterable-fields';
import { GENDER_OPTIONS, LANGUAGE_OPTIONS, META_TAG_OPTIONS } from '../config/filter-field-options';

export { GENDER_OPTIONS, LANGUAGE_OPTIONS, META_TAG_OPTIONS };

export type BooleanFilterValue = '' | 'true' | 'false';

export type ContactsFilters = {
  listName: string;
  listLeadIds: string[];
  listCustomFilter?: ContactMongoFilter;
  fields: Record<string, FilterValue>;
};

export const AGE_RANGE_BOUNDS = { min: 18, max: 65 } as const;

export const BOOLEAN_FILTER_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
] as const;

export const DEFAULT_CONTACTS_FILTERS: ContactsFilters = {
  listName: '',
  listLeadIds: [],
  listCustomFilter: undefined,
  fields: Object.fromEntries(
    FILTERABLE_FIELDS.map((field) => [field.key, field.defaultValue ?? ''])
  ),
};

export const CITY_OPTIONS = [
  'Romandie',
  'Zurich',
  'Zurich (EN)',
  'St. Gallen',
  'Basel',
  'Bern',
  'Family Zurich',
  'Winterthur',
  'Luzern',
] as const;

/**
 * Contact `Source` vocabulary. Single source of truth is the `source` field in
 * `../config/filterable-fields` (the registry drives the filter UI); this type
 * is derived from it so write paths (manual add, CSV import) can only produce
 * values that are actually filterable.
 */
export const CONTACT_SOURCE_OPTIONS = ['Manual', 'CsvImport', 'CrmApi', 'EshopApi'] as const;

export type ContactSource = (typeof CONTACT_SOURCE_OPTIONS)[number];

export const CMS_CATEGORY_OPTIONS = [
  'Guides',
  'People',
  'Out of town',
  'Contest',
  'Family',
] as const;

export const MARKET_CATEGORY_OPTIONS = [
  'Living',
  'Kultur',
  'Nightlife',
  'Apartments',
] as const;

export const SUBSCRIPTION_NAME_OPTIONS = ['Free', 'Ron Pass'] as const;

export const USER_TYPE_OPTIONS = ['Individual', 'Company'] as const;

export const getLanguageLabel = (value: string): string =>
  LANGUAGE_OPTIONS.find((option) => option.value === value)?.label ?? value;

export const getGenderLabel = (value: string): string =>
  GENDER_OPTIONS.find((option) => option.value === value)?.label ?? value;

export const getBooleanFilterLabel = (value: BooleanFilterValue): string => {
  if (value === 'true') return 'Yes';
  if (value === 'false') return 'No';
  return '';
};

export const parseBooleanFilterValue = (
  value: BooleanFilterValue
): boolean | undefined => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

const isRangeValue = (value: unknown): value is { from?: string; to?: string } =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFieldValueActive = (field: FilterFieldConfig, value: FilterValue | undefined): boolean => {
  if (value === undefined || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  if (field.defaultValue !== undefined) {
    return JSON.stringify(value) !== JSON.stringify(field.defaultValue);
  }
  if (isRangeValue(value)) return Boolean(value.from || value.to);
  return true;
};

export const hasActiveContactsFilters = (
  filters: ContactsFilters,
  registry: FilterFieldConfig[] = FILTERABLE_FIELDS
): boolean => {
  const hasListFilter =
    Boolean(filters.listName) ||
    filters.listLeadIds.length > 0 ||
    Boolean(filters.listCustomFilter && Object.keys(filters.listCustomFilter).length > 0);

  const hasFieldFilter = registry.some((field) =>
    isFieldValueActive(field, filters.fields[field.key])
  );

  return hasListFilter || hasFieldFilter;
};

const formatChipValue = (field: FilterFieldConfig, value: FilterValue): string => {
  if (Array.isArray(value)) {
    if (value.length === 2 && typeof value[0] === 'number') {
      const [min, max] = value as [number, number];
      return `${min}–${max}`;
    }
    if (value.every((entry) => typeof entry === 'string')) {
      return value
        .map((entry) => field.options?.find((option) => option.value === entry)?.label ?? entry)
        .join(', ');
    }
  }
  if (isRangeValue(value)) {
    return `${value.from || '…'} – ${value.to || '…'}`;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string' && field.options) {
    return field.options.find((option) => option.value === value)?.label ?? value;
  }
  return String(value);
};

export type ContactsFilterChip = { fieldKey: string; label: string };

export const getContactsFilterChips = (
  filters: ContactsFilters,
  registry: FilterFieldConfig[] = FILTERABLE_FIELDS
): ContactsFilterChip[] => {
  const chips: ContactsFilterChip[] = [];

  if (filters.listName) {
    chips.push({ fieldKey: 'listName', label: `List: ${filters.listName}` });
  }

  for (const field of registry) {
    const value = filters.fields[field.key];
    if (!isFieldValueActive(field, value)) continue;
    chips.push({ fieldKey: field.key, label: `${field.label}: ${formatChipValue(field, value)}` });
  }

  return chips;
};

export const buildContactsFilterFieldsDefaults = (
  registry: FilterFieldConfig[]
): Record<string, FilterValue> =>
  Object.fromEntries(registry.map((field) => [field.key, field.defaultValue ?? '']));

export const buildDefaultContactsFilters = (
  registry: FilterFieldConfig[] = FILTERABLE_FIELDS
): ContactsFilters => ({
  listName: '',
  listLeadIds: [],
  listCustomFilter: undefined,
  fields: buildContactsFilterFieldsDefaults(registry),
});
